import { IconGripVertical } from "@tabler/icons-react";
import { clsx } from "clsx";
import { forwardRef } from "react";
import type { ComponentPropsWithoutRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { mergeRefs } from "src/common/utils/mergeRefs.js";

type Company = { id: string; name: string };
type DroppedItem = { id: string };

// Must match the item type HierarchyTable's own drag sources/drop targets
// register internally (ExoKit, not exported) — kept as one named constant so
// a change here can't silently drift out of sync with itself across this file.
const DND_ITEM_TYPE = "item";

const DraggableCompanyRow = ({ company }: { company: Company }) => {
	const [{ opacity }, dragRef] = useDrag(
		() => ({
			type: DND_ITEM_TYPE,
			item: { id: company.id, label: company.name, children: [] },
			collect: (monitor) => ({ opacity: monitor.isDragging() ? 0.5 : 1 }),
		}),
		[company],
	);

	return (
		<li
			ref={(node) => {
				dragRef(node);
			}}
			className="flex cursor-move items-center gap-2 border-grey-200 border-b px-3 py-2 text-sm last:border-b-0"
			style={{ opacity }}
		>
			<IconGripVertical size={16} className="shrink-0 text-grey-400" />
			{company.name}
		</li>
	);
};

/**
 * Drag source for placing a company into a `HierarchyTable` rendered with
 * `standalone={false}` and wrapped in the same external `DndProvider` — the
 * item type and shape (`{id, label, children}`) match what `HierarchyTable`'s
 * own drop targets expect. Also a drop target itself, so dragging a placed
 * company back here removes it — unlike adding one (a bare `children: []`
 * leaf), removing can take a whole branch, so the caller confirms first.
 */
type Props = Omit<ComponentPropsWithoutRef<"div">, "children"> & {
	companies: Company[];
	emptyLabel: string;
	dropHintLabel: string;
	onDropCompany: (item: DroppedItem) => void;
	canDropCompany?: (item: DroppedItem) => boolean;
};

export const DraggableCompanyList = forwardRef<HTMLDivElement, Props>(
	(
		{
			companies,
			emptyLabel,
			dropHintLabel,
			onDropCompany,
			canDropCompany = () => true,
			className,
			...rest
		},
		ref,
	) => {
		const [{ isOverAndCanDrop }, dropRef] = useDrop(
			() => ({
				accept: DND_ITEM_TYPE,
				canDrop: canDropCompany,
				drop: onDropCompany,
				collect: (monitor) => ({
					isOverAndCanDrop: monitor.isOver() && monitor.canDrop(),
				}),
			}),
			[onDropCompany, canDropCompany],
		);

		return (
			<div
				{...rest}
				ref={mergeRefs([dropRef, ref ?? null])}
				className={clsx(
					"flex h-full flex-col",
					isOverAndCanDrop && "bg-blue-50 outline-2 outline-blue-300",
					className,
				)}
			>
				{companies.length === 0 ? (
					<p className="p-3 text-grey-500 text-sm">{emptyLabel}</p>
				) : (
					<ul className="min-h-0 flex-1 overflow-y-auto">
						{companies.map((company) => (
							<DraggableCompanyRow key={company.id} company={company} />
						))}
					</ul>
				)}
				{isOverAndCanDrop && (
					<p className="border-blue-300 border-t border-dashed p-3 text-blue-600 text-xs">
						{dropHintLabel}
					</p>
				)}
			</div>
		);
	},
);
DraggableCompanyList.displayName = "DraggableCompanyList";
